/**
 * /api/v1/DispatchActivity — activity CRUD + subresources (workers/assets/tasks)
 * + execution (capture + roll-over).
 */

import axios from 'axios';
import config from '../../../../../config';
import { unwrap } from '../http';
import type {
  BackendDispatchActivity,
  BackendDispatchActivityWorker,
  BackendDispatchActivityAsset,
  BackendDispatchActivityTask,
  CreateUpdateResponse,
  DeleteResponse,
  TupleResponse,
  DispatchActivityCreateRequest,
  DispatchActivityUpdateRequest,
  DispatchActivityStatusUpdateRequest,
  DispatchActivityWorkerCreateRequest,
  DispatchActivityAssetCreateRequest,
  DispatchActivityTaskCreateRequest,
  DispatchActivityTaskStatusUpdateRequest,
  CaptureActivityRequest,
  CaptureDayCartageRequest,
  RollOverRequest,
} from '../types';

// Absolute URL pattern (matches assetService / leadService project convention).
const BASE = `${config.api.API_URL}/DispatchActivity`;

export const dispatchActivityService = {
  /* ── Activity CRUD ─────────────────────────────────────────────────── */
  getSingle: (id: string) =>
    axios.get<BackendDispatchActivity>(`${BASE}/${id}`).then(unwrap),

  getAll: (pageNumber = 1, pageSize = 50) =>
    axios
      .get<TupleResponse<BackendDispatchActivity>>(BASE, { params: { pageNumber, pageSize } })
      .then(unwrap),

  getByDate: (date: string) =>
    axios
      .get<BackendDispatchActivity[]>(`${BASE}/by-date`, { params: { date } })
      .then(unwrap),

  getByCrewAndDate: (crewId: string, date: string) =>
    axios
      .get<BackendDispatchActivity[]>(`${BASE}/by-crew`, { params: { crewId, date } })
      .then(unwrap),

  getStandaloneByDate: (date: string) =>
    axios
      .get<BackendDispatchActivity[]>(`${BASE}/standalone`, { params: { date } })
      .then(unwrap),

  getByJob: (jobId: string) =>
    axios.get<BackendDispatchActivity[]>(`${BASE}/by-job/${jobId}`).then(unwrap),

  getContinuationChain: (rootId: string) =>
    axios.get<BackendDispatchActivity[]>(`${BASE}/chain/${rootId}`).then(unwrap),

  create: (body: DispatchActivityCreateRequest) =>
    axios.post<CreateUpdateResponse<BackendDispatchActivity>>(BASE, body).then(unwrap),

  update: (id: string, body: DispatchActivityUpdateRequest) =>
    axios
      .put<CreateUpdateResponse<BackendDispatchActivity>>(`${BASE}/${id}`, body)
      .then(unwrap),

  /** Cancellation REQUIRES cancellationNotes in the body. Backend rejects otherwise. */
  updateStatus: (id: string, body: DispatchActivityStatusUpdateRequest) =>
    axios
      .put<CreateUpdateResponse<BackendDispatchActivity>>(`${BASE}/${id}/status`, body)
      .then(unwrap),

  delete: (id: string) =>
    axios.delete<DeleteResponse>(`${BASE}/${id}`).then(unwrap),

  /** Restore a soft-deleted activity. Returns as standalone (admin re-pins to crew). */
  restore: (id: string) =>
    axios
      .post<CreateUpdateResponse<BackendDispatchActivity>>(`${BASE}/${id}/restore`)
      .then(unwrap),

  /* ── Workers ──────────────────────────────────────────────────────── */
  getWorkers: (activityId: string) =>
    axios.get<BackendDispatchActivityWorker[]>(`${BASE}/${activityId}/workers`).then(unwrap),

  addWorker: (activityId: string, body: DispatchActivityWorkerCreateRequest) =>
    axios
      .post<CreateUpdateResponse<BackendDispatchActivityWorker>>(`${BASE}/${activityId}/workers`, body)
      .then(unwrap),

  removeWorker: (workerId: string) =>
    axios.delete<DeleteResponse>(`${BASE}/workers/${workerId}`).then(unwrap),

  activateWorker: (workerId: string) =>
    axios
      .put<CreateUpdateResponse<BackendDispatchActivityWorker>>(`${BASE}/workers/${workerId}/activate`)
      .then(unwrap),

  deactivateWorker: (workerId: string) =>
    axios
      .put<CreateUpdateResponse<BackendDispatchActivityWorker>>(`${BASE}/workers/${workerId}/deactivate`)
      .then(unwrap),

  /* ── Assets ───────────────────────────────────────────────────────── */
  getAssets: (activityId: string) =>
    axios.get<BackendDispatchActivityAsset[]>(`${BASE}/${activityId}/assets`).then(unwrap),

  addAsset: (activityId: string, body: DispatchActivityAssetCreateRequest) =>
    axios
      .post<CreateUpdateResponse<BackendDispatchActivityAsset>>(`${BASE}/${activityId}/assets`, body)
      .then(unwrap),

  removeAsset: (assetRowId: string) =>
    axios.delete<DeleteResponse>(`${BASE}/assets/${assetRowId}`).then(unwrap),

  activateAsset: (assetRowId: string) =>
    axios
      .put<CreateUpdateResponse<BackendDispatchActivityAsset>>(`${BASE}/assets/${assetRowId}/activate`)
      .then(unwrap),

  deactivateAsset: (assetRowId: string) =>
    axios
      .put<CreateUpdateResponse<BackendDispatchActivityAsset>>(`${BASE}/assets/${assetRowId}/deactivate`)
      .then(unwrap),

  /* ── Tasks ────────────────────────────────────────────────────────── */
  getTasks: (activityId: string) =>
    axios.get<BackendDispatchActivityTask[]>(`${BASE}/${activityId}/tasks`).then(unwrap),

  /** Honours JOB-MATCH + JOB-INHERIT business rules server-side. */
  addTask: (activityId: string, body: DispatchActivityTaskCreateRequest) =>
    axios
      .post<CreateUpdateResponse<BackendDispatchActivityTask>>(`${BASE}/${activityId}/tasks`, body)
      .then(unwrap),

  updateTaskStatus: (taskId: string, body: DispatchActivityTaskStatusUpdateRequest) =>
    axios
      .put<CreateUpdateResponse<BackendDispatchActivityTask>>(`${BASE}/tasks/${taskId}/status`, body)
      .then(unwrap),

  removeTask: (taskId: string) =>
    axios.delete<DeleteResponse>(`${BASE}/tasks/${taskId}`).then(unwrap),

  /* ── Execution capture + roll-over (Phase 5c) ─────────────────────── */

  /**
   * End-of-day capture finalization. Idempotent until invoiced — re-capture
   * deletes uninvoiced labour/cartage and regenerates. Blocked once any
   * downstream row is invoiced (response carries an explanatory message).
   */
  captureActivity: (body: CaptureActivityRequest) =>
    axios
      .post<CreateUpdateResponse<BackendDispatchActivity>>(`${BASE}/capture`, body)
      .then(unwrap),

  /**
   * Manual per-task roll-over to continuation activities on future dates.
   * Result is the list of continuation activity ids touched (created or reused).
   */
  rollOver: (body: RollOverRequest) =>
    axios
      .post<CreateUpdateResponse<string[]>>(`${BASE}/roll-over`, body)
      .then(unwrap),

  /**
   * Day-level cartage capture. Each vehicle run on the day becomes ONE
   * cartage job (odometer-derived or direct km × matrix rate − trip discount)
   * allocated across the WorkOrders the vehicle serviced. This is the model
   * invoicing reads (one CartageJobSplit per WorkOrder). Idempotent until
   * invoiced — re-capture soft-deletes + recreates the prior Date+Asset job.
   * Returns the created/updated cartage-job count.
   */
  captureDayCartage: (body: CaptureDayCartageRequest) =>
    axios
      .post<CreateUpdateResponse<number>>(`${BASE}/day-cartage`, body)
      .then(unwrap),

  /* ── Capture reset (TEST/ADMIN — server gates to the reset-admin) ───── */
  /** Un-capture one activity → capturable again; deletes its uninvoiced rows. */
  uncapture: (activityId: string) =>
    axios
      .post<CreateUpdateResponse<number>>(`${BASE}/${activityId}/uncapture`)
      .then(unwrap),
  /** Reset ALL captures for a day (yyyy-MM-dd). */
  resetDay: (date: string) =>
    axios
      .post<CreateUpdateResponse<number>>(`${BASE}/reset-day`, null, {
        params: { date },
      })
      .then(unwrap),
};
